import { useEffect, useState } from "react";
import { Plus, Trash2, Loader2, Pencil, BookOpen } from "lucide-react";
import api, { fileUrl } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import ImageUpload from "@/components/ImageUpload";
import MultiImageUpload from "@/components/MultiImageUpload";
import SectionsEditor from "@/components/SectionsEditor";

const CATEGORIES = ["House Rules", "Local Guide", "Facilities", "Dining", "Room Info", "Activities"];
const empty = { category: "Local Guide", title: "", description: "", image_url: null, gallery: [], sections: [], link: "" };

export default function DirectoryPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);

  const load = () => api.get("/directory").then((r) => { setItems(r.data); setLoading(false); });
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm(empty); setOpen(true); };
  const openEdit = (it) => { setEditing(it); setForm({ ...empty, ...it, gallery: it.gallery || [], sections: it.sections || [] }); setOpen(true); };

  const save = async () => {
    if (!form.title) return toast.error("Title is required");
    try {
      if (editing) await api.put(`/directory/${editing.id}`, form);
      else await api.post("/directory", form);
      toast.success("Saved");
      setOpen(false);
      load();
    } catch { toast.error("Could not save"); }
  };

  const del = async (id) => { await api.delete(`/directory/${id}`); toast.success("Removed"); load(); };

  return (
    <div className="space-y-6" data-testid="directory-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl text-foreground mb-1">Directory & Guides</h1>
          <p className="text-muted-foreground text-sm">Each item gets its own rich page — add a gallery and multiple sections.</p>
        </div>
        <Button onClick={openNew} data-testid="add-directory-btn" className="bg-primary text-primary-foreground"><Plus className="w-4 h-4 mr-2" /> Add item</Button>
      </div>

      {loading ? <Loader2 className="w-5 h-5 animate-spin text-accent" /> : items.length === 0 ? (
        <div className="text-center py-20 border border-dashed border-border rounded-md">
          <BookOpen className="w-8 h-8 text-muted-foreground mx-auto mb-3" strokeWidth={1.5} />
          <p className="text-muted-foreground text-sm">No directory items yet.</p>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.map((it) => (
            <div key={it.id} className="bg-card border border-border rounded-md overflow-hidden hover-lift" data-testid={`directory-item-${it.id}`}>
              {it.image_url && <img src={fileUrl(it.image_url)} alt={it.title} className="w-full h-36 object-cover" />}
              <div className="p-4">
                <span className="text-xs uppercase tracking-widest text-accent">{it.category}</span>
                <h3 className="font-serif text-lg text-foreground mt-1">{it.title}</h3>
                <p className="text-sm text-muted-foreground mt-1 line-clamp-2">{it.description}</p>
                <p className="text-xs text-muted-foreground mt-2">{(it.gallery?.length || 0)} photos · {(it.sections?.length || 0)} sections</p>
                <div className="flex gap-2 mt-4">
                  <Button size="sm" variant="outline" onClick={() => openEdit(it)} data-testid={`edit-directory-${it.id}`}><Pencil className="w-3.5 h-3.5" /></Button>
                  <Button size="sm" variant="outline" onClick={() => del(it.id)} data-testid={`delete-directory-${it.id}`} className="text-red-600"><Trash2 className="w-3.5 h-3.5" /></Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg max-h-[88vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-serif text-xl">{editing ? "Edit item" : "Add directory item"}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Category</Label>
              <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} data-testid="directory-category" className="mt-1.5 w-full h-10 rounded-md border border-input px-3 text-sm bg-background">
                {CATEGORIES.map((c) => <option key={c}>{c}</option>)}
              </select>
            </div>
            <div><Label>Title</Label><Input data-testid="directory-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="mt-1.5" /></div>
            <div><Label>Short description</Label><Textarea data-testid="directory-desc" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={2} className="mt-1.5" /></div>
            <div><Label>Link (optional)</Label><Input data-testid="directory-link" value={form.link || ""} onChange={(e) => setForm({ ...form, link: e.target.value })} className="mt-1.5" placeholder="https://" /></div>
            <div><Label className="mb-1.5 block">Cover image</Label><ImageUpload value={form.image_url} onChange={(v) => setForm({ ...form, image_url: v })} testId="directory-image" /></div>
            <div><Label className="mb-1.5 block">Gallery</Label><MultiImageUpload value={form.gallery} onChange={(v) => setForm({ ...form, gallery: v })} testId="directory-gallery" /></div>
            <SectionsEditor value={form.sections} onChange={(v) => setForm({ ...form, sections: v })} testId="directory-sections" />
            <Button onClick={save} data-testid="save-directory-btn" className="w-full bg-primary text-primary-foreground">Save item</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
